// src/UpgradePage.js

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from './supabaseClient';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Button,
  Divider,
  Paper,
  Tabs,
  Tab,
} from '@mui/material';

export default function UpgradePage() {
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [companyId, setCompanyId] = React.useState(null);
  const [currentSub, setCurrentSub] = React.useState(null);
  const [liveAgentSub, setLiveAgentSub] = React.useState(null);
  const [userEmail, setUserEmail] = React.useState('');
  const [userId, setUserId] = React.useState(null);
  const [activeTab, setActiveTab] = React.useState(0);

  const publishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '';
  const pricingTableId = process.env.REACT_APP_STRIPE_PRICING_TABLE_ID || '';
  const liveAgentPricingTableId = process.env.REACT_APP_STRIPE_LIVE_AGENT_PRICING_TABLE_ID || '';
  const portalConfigId = process.env.REACT_APP_STRIPE_PORTAL_CONFIGURATION_ID || '';

  React.useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          setError('Not authenticated');
          setLoading(false);
          return;
        }
        setUserId(user.id);
        setUserEmail(user.email || '');

        // 1) Resolve company_id
        const { data: prof, error: profErr } = await supabase
          .from('user_profiles')
          .select('company_id')
          .eq('user_id', user.id)
          .maybeSingle();
        if (profErr) throw profErr;
        const cid = prof?.company_id || null;
        setCompanyId(cid);

        // 2) Load current active subscriptions (both AI and Live Agent)
        let sub = null;
        let laSub = null;
        if (cid) {
          const [{ data: aiData }, { data: laData }] = await Promise.all([
            supabase
              .from('company_subscriptions')
              .select('*')
              .eq('company_id', cid)
              .eq('status', 'active')
              .order('created_at', { ascending: false })
              .limit(1)
              .maybeSingle(),
            supabase
              .from('company_live_agent_subscriptions')
              .select('*')
              .eq('company_id', cid)
              .eq('status', 'active')
              .maybeSingle()
          ]);
          sub = aiData || null;
          laSub = laData || null;
        }
        setCurrentSub(sub);
        setLiveAgentSub(laSub);
      } catch (e) {
        setError(e.message || String(e));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Ensure Stripe Pricing Table script is present
  React.useEffect(() => {
    const src = 'https://js.stripe.com/v3/pricing-table.js';
    const existing = document.querySelector(`script[src="${src}"]`);
    if (!existing) {
      const s = document.createElement('script');
      s.src = src;
      s.async = true;
      document.body.appendChild(s);
    }
  }, []);

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', maxWidth: 1400, mx: 'auto' }}>
        <Typography variant="h5">Upgrade Plan</Typography>
        <Button size="small" variant="outlined" onClick={() => navigate('/chat')}>Back to Chat</Button>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
          {/* Current Subscriptions Status */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom>Current Subscriptions</Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {currentSub ? (
                <Chip
                  label={`AI Package: ${currentSub.package_name || 'Plan'} ($${(currentSub.price_usd ?? 0).toFixed(2)}/mo)`}
                  color="success"
                />
              ) : (
                <Chip label="No AI Package" variant="outlined" />
              )}
              {liveAgentSub ? (
                <Chip
                  label={`Live Agent: ${liveAgentSub.seat_count} ${liveAgentSub.seat_count === 1 ? 'seat' : 'seats'} ($${(liveAgentSub.seat_count * 19).toFixed(2)}/mo)`}
                  color="secondary"
                />
              ) : (
                <Chip label="No Live Agent" variant="outlined" />
              )}
            </Box>
            {!currentSub && !liveAgentSub && (
              <Alert severity="info" sx={{ mt: 2 }}>
                No active subscriptions. Choose a plan below to get started!
              </Alert>
            )}
          </Paper>

          {/* Tabs for different product types */}
          <Paper sx={{ mb: 2 }}>
            <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} centered>
              <Tab label="AI Automation Packages" />
              <Tab label="Live Human Agent" />
            </Tabs>
          </Paper>

          {/* AI Packages Pricing Table */}
          {activeTab === 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ textAlign: 'center', mb: 3 }}>
                AI Automation Packages
              </Typography>
              {!publishableKey || !pricingTableId ? (
                <Alert severity="warning">
                  Stripe Pricing Table not configured. Please set REACT_APP_STRIPE_PUBLISHABLE_KEY and REACT_APP_STRIPE_PRICING_TABLE_ID.
                </Alert>
              ) : (
                portalConfigId ? (
                  <stripe-pricing-table
                    pricing-table-id={pricingTableId}
                    publishable-key={publishableKey}
                    client-reference-id={(companyId || userId || '').toString()}
                    customer-email={userEmail || ''}
                    customer-portal-configuration={portalConfigId}
                  />
                ) : (
                  <stripe-pricing-table
                    pricing-table-id={pricingTableId}
                    publishable-key={publishableKey}
                    client-reference-id={(companyId || userId || '').toString()}
                    customer-email={userEmail || ''}
                  />
                )
              )}
            </Box>
          )}

          {/* Live Agent Pricing Table */}
          {activeTab === 1 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ textAlign: 'center', mb: 1 }}>
                Live Human Agent Support
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mb: 3 }}>
                Live support via WhatsApp, Web Chat, Instagram, Messenger, and Telegram
              </Typography>

              {!publishableKey ? (
                <Alert severity="warning">
                  Stripe Publishable Key not configured.
                </Alert>
              ) : (
                <stripe-pricing-table
                  pricing-table-id={liveAgentPricingTableId}
                  publishable-key={publishableKey}
                  client-reference-id={(companyId || userId || '').toString()}
                  customer-email={userEmail || ''}
                />
              )}

              {liveAgentSub && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  You already have {liveAgentSub.seat_count} Live Agent {liveAgentSub.seat_count === 1 ? 'seat' : 'seats'}.
                  You can add more seats using the table above.
                </Alert>
              )}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}



